import React, {useCallback, useEffect, useState, useRef} from 'react';
import {View, ScrollView, StyleSheet, TouchableOpacity, Image, Alert, Platform, PermissionsAndroid} from 'react-native';
import {
  Text, TextInput, Button, Switch,
  IconButton, Divider, Dialog, Portal, ActivityIndicator,
} from 'react-native-paper';
import {NativeModules} from 'react-native';
const {AudioRecorder} = NativeModules;
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {launchImageLibrary} from 'react-native-image-picker';
import {useOrderStore} from '../../store/useOrderStore';
import {useConfigStore} from '../../store/useConfigStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {measurementApi} from '../../services/api/measurementApi';
import {priceBookApi} from '../../services/api/priceBookApi';
import {workAssignmentApi} from '../../services/api/workAssignmentApi';
import DatePickerInput from '../../components/common/DatePickerInput';
import WorkAssignmentModal from './WorkAssignmentModal';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';

const OrderLineDetailScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const params = route.params;

  const editLine = params.editLine;
  const isEditMode = !!editLine;

  const {createOrder, addLineToOrder, updateLine, uploadLineImages, uploadLineAudio, deleteLineAudio, updateLineAudioNote} = useOrderStore();
  const {getProperty} = useConfigStore();
  const {orgUnits, selectedOrgUnitId} = useOrgUnitStore();
  const effectiveOrgUnitId = orgUnits.length === 1 ? orgUnits[0].id : selectedOrgUnitId;
  const measurementUnit = getProperty('ORDER', 'MEASUREMENT_UNIT', effectiveOrgUnitId) || 'INCHES';
  const unitLabel = measurementUnit === 'CMS' ? 'cms' : 'in';
  // Initialize state — pre-populate from editLine if in edit mode
  const [itemName, setItemName] = useState(
    isEditMode ? (editLine.itemName || '') : (params.catalogItemName + ' #1'),
  );
  const [serviceCatalogItems, setServiceCatalogItems] = useState([]);
  const [serviceCatalogItemId, setServiceCatalogItemId] = useState(
    isEditMode ? (editLine.serviceCatalogItemId || null) : null,
  );
  const [specialInstructions, setSpecialInstructions] = useState(
    isEditMode ? (editLine.specialInstructions || '') : '',
  );
  const [videoLink, setVideoLink] = useState(
    isEditMode ? (editLine.videoLink || '') : '',
  );
  const [measurementDressGiven, setMeasurementDressGiven] = useState(
    isEditMode ? (editLine.measurementDressGiven || false) : false,
  );
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState(
    isEditMode ? String(editLine.unitPrice || '') : '',
  );
  const [deliveryDate, setDeliveryDate] = useState(
    isEditMode ? (editLine.deliveryDate || '') : (params.deliveryDate || ''),
  );
  const [trialDates, setTrialDates] = useState(
    isEditMode ? (editLine.trialDates || []) : [],
  );
  const [functionDate, setFunctionDate] = useState(
    isEditMode ? (editLine.functionDate || '') : '',
  );
  const [urgentLine, setUrgentLine] = useState(
    isEditMode ? (editLine.urgent || false) : (params.urgent || false),
  );
  const [salesStaffId, setSalesStaffId] = useState(
    isEditMode ? editLine.salesStaffId : null,
  );
  const [salesStaffName, setSalesStaffName] = useState(
    isEditMode ? (editLine.salesStaffName || '') : '',
  );
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [selectedAddOns, setSelectedAddOns] = useState(() => {
    if (isEditMode && editLine.addOnLines && editLine.addOnLines.length > 0) {
      return editLine.addOnLines.map(ao => ({
        id: ao.addOnId,
        name: ao.itemName,
        price: String(ao.unitPrice),
      }));
    }
    return [];
  });
  const [measurementProfileId, setMeasurementProfileId] = useState(
    isEditMode && editLine.instances && editLine.instances.length > 0
      ? editLine.instances[0].measurementProfileId : null,
  );
  const [measurementProfileName, setMeasurementProfileName] = useState(
    isEditMode && editLine.instances && editLine.instances.length > 0
      ? (editLine.instances[0].measurementProfileName || '') : '',
  );
  const [optionSelections, setOptionSelections] = useState(() => {
    if (isEditMode && editLine.instances && editLine.instances.length > 0) {
      const sel = editLine.instances[0].optionSelections || editLine.instances[0].stitchFeatureSelections;
      if (sel) {
        try { return JSON.parse(sel); } catch (e) { return []; }
      }
    }
    return [];
  });
  const [clothImages, setClothImages] = useState(() => {
    if (isEditMode && editLine.clothImageUrls && editLine.clothImageUrls.length > 0) {
      return editLine.clothImageUrls.map(url => ({uri: url, existing: true}));
    }
    return [];
  });
  const [patternImages, setPatternImages] = useState(() => {
    if (isEditMode && editLine.patternImageUrls && editLine.patternImageUrls.length > 0) {
      return editLine.patternImageUrls.map(url => ({uri: url, existing: true}));
    }
    return [];
  });
  const [saving, setSaving] = useState(false);

  // Audio recording state
  const [audioModalVisible, setAudioModalVisible] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState('00:00');
  const [audioUploading, setAudioUploading] = useState(false);
  const [recordedPath, setRecordedPath] = useState(null);
  const [audioInstructions, setAudioInstructions] = useState(
    isEditMode ? (editLine.audioInstructions || []) : [],
  );
  const [playingIndex, setPlayingIndex] = useState(null);
  const [newAudioIndices, setNewAudioIndices] = useState([]);
  const [noteDialogVisible, setNoteDialogVisible] = useState(false);
  const [noteDialogIndex, setNoteDialogIndex] = useState(null);
  const [noteText, setNoteText] = useState('');

  useEffect(() => {
    measurementApi.listCatalogItems(false, 'SERVICE')
      .then(res => setServiceCatalogItems(res.data?.data || []))
      .catch(() => {});
  }, []);

  // Warn on back navigation if there are unsaved audio uploads
  useEffect(() => {
    if (!isEditMode || newAudioIndices.length === 0) return;
    const unsubscribe = navigation.addListener('beforeRemove', (e) => {
      e.preventDefault();
      Alert.alert(
        'Unsaved Audio',
        'You have new audio recordings. Go back without saving will delete them. Do you want to discard?',
        [
          {text: 'Stay', style: 'cancel'},
          {
            text: 'Discard & Go Back', style: 'destructive',
            onPress: async () => {
              // Delete orphan audio files from server (newest first to keep indices valid)
              const sorted = [...newAudioIndices].sort((a, b) => b - a);
              for (const idx of sorted) {
                try { await deleteLineAudio(params.orderId, editLine.id, idx); } catch (_) {}
              }
              navigation.dispatch(e.data.action);
            },
          },
        ],
      );
    });
    return unsubscribe;
  }, [navigation, isEditMode, newAudioIndices, deleteLineAudio, params.orderId, editLine]);

  useEffect(() => {
    if (route.params?.selectedAddOns) {
      setSelectedAddOns(route.params.selectedAddOns);
    }
    if (route.params?.salesStaffId) {
      setSalesStaffId(route.params.salesStaffId);
      setSalesStaffName(route.params.salesStaffName || '');
    }
    if (route.params?.measurementProfileId) {
      setMeasurementProfileId(route.params.measurementProfileId);
      setMeasurementProfileName(route.params.measurementProfileName || '');
    }
    if (route.params?.optionSelections) {
      setOptionSelections(route.params.optionSelections);
    }
  }, [route.params?.selectedAddOns, route.params?.salesStaffId, route.params?.salesStaffName,
      route.params?.measurementProfileId, route.params?.measurementProfileName,
      route.params?.optionSelections]);

  useEffect(() => {
    if (!isEditMode && params.catalogItemId) {
      priceBookApi.resolvePrice(params.catalogItemId, serviceCatalogItemId)
        .then(res => {
          const price = res.data?.data?.unitPrice;
          if (price != null) {
            setUnitPrice(String(price));
          }
        })
        .catch(() => {});
    }
  }, [isEditMode, serviceCatalogItemId, params.catalogItemId]);

  const fetchAssignments = useCallback(() => {
    if (!isEditMode || !editLine.instances?.length) return;
    setAssignmentsLoading(true);
    workAssignmentApi.getByInstance(editLine.instances[0].id)
      .then(res => setAssignments(res.data?.data || []))
      .catch(() => {})
      .finally(() => setAssignmentsLoading(false));
  }, [isEditMode, editLine]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const addOnsTotal = selectedAddOns.reduce((sum, a) => sum + (parseFloat(a.price) || 0), 0);
  const stitchingTotal = (parseFloat(unitPrice) || 0) * quantity;

  const pickImage = useCallback(async (setter) => {
    const result = await launchImageLibrary({
      mediaType: 'photo', maxWidth: 1024, maxHeight: 1024, quality: 0.8,
    });
    if (result.assets && result.assets.length > 0) {
      setter(prev => [...prev, result.assets[0]]);
    }
  }, []);

  const requestMicPermission = useCallback(async () => {
    if (Platform.OS === 'android') {
      const granted = await PermissionsAndroid.request(
        PermissionsAndroid.PERMISSIONS.RECORD_AUDIO,
        {title: 'Microphone Permission', message: 'App needs microphone access to record audio instructions.'},
      );
      return granted === PermissionsAndroid.RESULTS.GRANTED;
    }
    return true;
  }, []);

  const durationInterval = useRef(null);

  const openAudioModal = useCallback(async () => {
    if (audioInstructions.length >= 3) {
      Alert.alert('Limit Reached', 'Maximum 3 audio recordings allowed per item.');
      return;
    }
    if (!AudioRecorder) {
      Alert.alert('Unavailable', 'Audio recording is not available. Please rebuild the app.');
      return;
    }
    const hasPermission = await requestMicPermission();
    if (!hasPermission) {
      Alert.alert('Permission Denied', 'Microphone permission is required to record audio.');
      return;
    }
    setRecordedPath(null);
    setRecordingDuration('00:00');
    setIsRecording(false);
    setAudioModalVisible(true);
  }, [audioInstructions.length, requestMicPermission]);

  const startRecording = useCallback(async () => {
    try {
      const path = await AudioRecorder.startRecording();
      setRecordedPath(path);
      setIsRecording(true);
      const startMs = Date.now();
      durationInterval.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startMs) / 1000);
        const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
        const ss = String(elapsed % 60).padStart(2, '0');
        setRecordingDuration(`${mm}:${ss}`);
      }, 500);
    } catch (e) {
      Alert.alert('Error', 'Failed to start recording');
    }
  }, []);

  const stopAndUpload = useCallback(async () => {
    try {
      if (durationInterval.current) {
        clearInterval(durationInterval.current);
        durationInterval.current = null;
      }
      const path = await AudioRecorder.stopRecording();
      setIsRecording(false);
      setAudioUploading(true);

      const updatedOrder = await uploadLineAudio(params.orderId, editLine.id, {
        uri: Platform.OS === 'android' ? 'file://' + path : path,
        type: 'audio/mp4',
        fileName: 'audio.mp4',
      });

      // Refresh audio instructions from the updated order
      const updatedLine = updatedOrder.lines?.find(l => l.id === editLine.id);
      const newInstructions = updatedLine?.audioInstructions || [];
      setAudioInstructions(newInstructions);

      // Track newly added index for orphan detection
      const newIdx = newInstructions.length - 1;
      setNewAudioIndices(prev => [...prev, newIdx]);

      setAudioModalVisible(false);

      // Open note dialog for the newly added audio
      setNoteDialogIndex(newIdx);
      setNoteText('');
      setNoteDialogVisible(true);
    } catch (e) {
      Alert.alert('Error', e.response?.data?.message || 'Failed to upload audio');
    } finally {
      setAudioUploading(false);
    }
  }, [uploadLineAudio, params.orderId, editLine]);

  const cancelRecording = useCallback(async () => {
    if (durationInterval.current) {
      clearInterval(durationInterval.current);
      durationInterval.current = null;
    }
    if (isRecording) {
      try {
        await AudioRecorder.cancelRecording();
      } catch (_) {}
      setIsRecording(false);
    }
    setAudioModalVisible(false);
  }, [isRecording]);

  const handleDeleteAudio = useCallback((index) => {
    Alert.alert('Delete Audio', 'Are you sure you want to delete this audio recording?', [
      {text: 'Cancel', style: 'cancel'},
      {
        text: 'Delete', style: 'destructive',
        onPress: async () => {
          try {
            const updatedOrder = await deleteLineAudio(params.orderId, editLine.id, index);
            const updatedLine = updatedOrder.lines?.find(l => l.id === editLine.id);
            setAudioInstructions(updatedLine?.audioInstructions || []);
            // Remove from new indices tracking and adjust remaining indices
            setNewAudioIndices(prev =>
              prev.filter(i => i !== index).map(i => i > index ? i - 1 : i),
            );
          } catch (e) {
            Alert.alert('Error', e.response?.data?.message || 'Failed to delete audio');
          }
        },
      },
    ]);
  }, [deleteLineAudio, params.orderId, editLine]);

  const handlePlayAudio = useCallback(async (url, index) => {
    if (!AudioRecorder) return;
    if (playingIndex === index) {
      try { await AudioRecorder.stopPlayback(); } catch (_) {}
      setPlayingIndex(null);
      return;
    }
    try {
      setPlayingIndex(index);
      await AudioRecorder.playAudio(url);
      setPlayingIndex(null);
    } catch (e) {
      setPlayingIndex(null);
    }
  }, [playingIndex]);

  const handleSaveNote = useCallback(async () => {
    if (noteDialogIndex == null) return;
    try {
      const updatedOrder = await updateLineAudioNote(
        params.orderId, editLine.id, noteDialogIndex, noteText.trim(),
      );
      const updatedLine = updatedOrder.lines?.find(l => l.id === editLine.id);
      setAudioInstructions(updatedLine?.audioInstructions || []);
    } catch (e) {
      Alert.alert('Error', e.response?.data?.message || 'Failed to save note');
    }
    setNoteDialogVisible(false);
    setNoteDialogIndex(null);
    setNoteText('');
  }, [noteDialogIndex, noteText, updateLineAudioNote, params.orderId, editLine]);

  const openNoteEditor = useCallback((index, existingNote) => {
    setNoteDialogIndex(index);
    setNoteText(existingNote || '');
    setNoteDialogVisible(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (serviceCatalogItems.length > 0 && !serviceCatalogItemId) {
      Alert.alert(t('common.error'), 'Please select a service type');
      return;
    }
    if (!unitPrice || parseFloat(unitPrice) <= 0) {
      Alert.alert(t('common.error'), 'Please enter stitching charge');
      return;
    }
    if (!deliveryDate) {
      Alert.alert(t('common.error'), 'Please select a delivery date');
      return;
    }
    if (!measurementProfileId) {
      Alert.alert(t('common.error'), 'Please add a measurement profile');
      return;
    }

    setSaving(true);
    try {
      if (isEditMode) {
        // Update existing line
        const existingClothUrls = clothImages.filter(img => img.existing).map(img => img.uri);
        const existingPatternUrls = patternImages.filter(img => img.existing).map(img => img.uri);
        const newClothImages = clothImages.filter(img => !img.existing);
        const newPatternImages = patternImages.filter(img => !img.existing);

        const updateData = {
          unitPrice: parseFloat(unitPrice),
          itemName,
          serviceCatalogItemId: serviceCatalogItemId || undefined,
          videoLink: videoLink || undefined,
          measurementDressGiven,
          deliveryDate: deliveryDate || undefined,
          trialDates: trialDates.length > 0 ? trialDates : undefined,
          functionDate: functionDate || undefined,
          urgent: urgentLine,
          salesStaffId: salesStaffId || undefined,
          specialInstructions: specialInstructions || undefined,
          measurementProfileId: measurementProfileId || undefined,
          addOns: selectedAddOns.length > 0
            ? selectedAddOns.map(a => ({addOnId: a.id, price: parseFloat(a.price)}))
            : undefined,
          optionSelections: optionSelections.length > 0
            ? JSON.stringify(optionSelections)
            : undefined,
          clothImageUrls: existingClothUrls.length > 0 ? existingClothUrls : [],
          patternImageUrls: existingPatternUrls.length > 0 ? existingPatternUrls : [],
        };

        await updateLine(params.orderId, editLine.id, updateData);

        // Upload newly picked images if any
        if (newClothImages.length > 0 || newPatternImages.length > 0) {
          try {
            await uploadLineImages(params.orderId, editLine.id,
              newClothImages.length > 0 ? newClothImages : null,
              newPatternImages.length > 0 ? newPatternImages : null);
          } catch (e) {
            // Image upload failure is non-critical
          }
        }

        setNewAudioIndices([]); // Clear so back-nav warning doesn't fire
        navigation.goBack();
      } else {
        // Create new line(s)
        const lineData = {
          catalogItemId: params.catalogItemId,
          quantity,
          unitPrice: parseFloat(unitPrice),
          itemName,
          serviceCatalogItemId: serviceCatalogItemId || undefined,
          videoLink: videoLink || undefined,
          measurementDressGiven,
          deliveryDate: deliveryDate || undefined,
          trialDates: trialDates.length > 0 ? trialDates : undefined,
          functionDate: functionDate || undefined,
          urgent: urgentLine,
          salesStaffId: salesStaffId || undefined,
          specialInstructions: specialInstructions || undefined,
          measurementProfileId: measurementProfileId || undefined,
          addOns: selectedAddOns.length > 0
            ? selectedAddOns.map(a => ({addOnId: a.id, price: parseFloat(a.price)}))
            : undefined,
          optionSelections: optionSelections.length > 0
            ? JSON.stringify(optionSelections)
            : undefined,
        };

        let order;
        if (params.orderId) {
          order = await addLineToOrder(params.orderId, lineData);
        } else {
          const today = new Date().toISOString().split('T')[0];
          const orderData = {
            orgUnitId: params.orgUnitId,
            customerId: params.customerId,
            orderDate: params.orderDate || today,
            deliveryDate: params.deliveryDate || deliveryDate || today,
            urgent: params.urgent || urgentLine || false,
            notes: params.notes || null,
            lines: [lineData],
          };
          order = await createOrder(orderData);
        }

        // Upload images if any
        const orderId = order.id;
        if ((clothImages.length > 0 || patternImages.length > 0) && order.lines && order.lines.length > 0) {
          const lastLine = order.lines[order.lines.length - 1];
          try {
            await uploadLineImages(orderId, lastLine.id,
              clothImages.length > 0 ? clothImages : null,
              patternImages.length > 0 ? patternImages : null);
          } catch (e) {
            // Image upload failure is non-critical
          }
        }

        navigation.navigate('OrderView', {orderId});
      }
    } catch (e) {
      Alert.alert(t('common.error'), e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  }, [isEditMode, editLine, params, quantity, unitPrice, itemName, serviceCatalogItems, serviceCatalogItemId, videoLink,
      measurementDressGiven, deliveryDate, trialDates, functionDate, urgentLine, salesStaffId,
      specialInstructions, measurementProfileId, selectedAddOns, optionSelections, clothImages,
      patternImages, createOrder, addLineToOrder, updateLine, uploadLineImages, navigation, t]);

  const optionSummary = optionSelections.length > 0
    ? (() => {
        const parts = optionSelections.map(s =>
          s.renderType === 'TEXT' ? s.textValue : (s.selectedOptions || []).map(o => o.name).join(', '),
        ).filter(Boolean).join(', ');
        return `Options (${optionSelections.length}): ${parts.length > 40 ? parts.substring(0, 40) + '...' : parts}`;
      })()
    : 'Select Options';

  const addTrialDate = () => setTrialDates(prev => [...prev, '']);
  const updateTrialDate = (idx, val) => setTrialDates(prev => prev.map((d, i) => i === idx ? val : d));
  const removeTrialDate = idx => setTrialDates(prev => prev.filter((_, i) => i !== idx));

  const pageTitle = isEditMode ? 'Edit Order Item' : params.catalogItemName;

  return (
    <View style={styles.container}>
      <GradientHeader title={pageTitle} onBack={() => navigation.goBack()} />

      <ScrollView style={styles.content} keyboardShouldPersistTaps="handled">
        {/* Item Name */}
        <Text variant="titleSmall" style={styles.sectionLabel}>Item Name</Text>
        <TextInput value={itemName} onChangeText={setItemName}
          mode="outlined" style={styles.input} outlineColor={colors.border} activeOutlineColor={colors.primary} />

        {/* Service Type */}
        {serviceCatalogItems.length > 0 && (
          <>
            <Text variant="titleSmall" style={styles.sectionLabel}>Service Type<Text style={styles.mandatory}> *</Text></Text>
            <View style={styles.serviceTypeRow}>
              {serviceCatalogItems.map(si => {
                const selected = serviceCatalogItemId === si.id;
                return (
                  <TouchableOpacity
                    key={si.id}
                    style={[styles.serviceTypeOption, selected && styles.serviceTypeOptionSelected]}
                    onPress={() => setServiceCatalogItemId(selected ? null : si.id)}
                    activeOpacity={0.7}>
                    <IconButton
                      icon={selected ? 'check-circle' : 'circle-outline'}
                      size={22}
                      iconColor={selected ? colors.primary : colors.textSecondary}
                      style={styles.serviceTypeIcon}
                    />
                    <Text style={[styles.serviceTypeLabel, selected && styles.serviceTypeLabelSelected]}>
                      {si.name}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </>
        )}

        <Divider style={styles.divider} />

        {/* Measurement buttons */}
        <Button mode="outlined" icon="ruler" onPress={() =>
          measurementProfileId
            ? navigation.push('MeasurementForm', {
                customerId: params.customerId,
                catalogItemId: params.catalogItemId,
                profileId: measurementProfileId,
                originalProfileName: measurementProfileName,
                returnTo: 'OrderLineDetail',
                mode: 'edit',
              })
            : navigation.push('MeasurementProfileSelector', {
                customerId: params.customerId,
                catalogItemId: params.catalogItemId,
              })}
          style={styles.actionButton}>
          {measurementProfileName ? `Measurement (${unitLabel}): ${measurementProfileName}` : `Add Measurement (${unitLabel}) *`}
        </Button>
        <Button mode="outlined" icon="cog" onPress={() =>
          navigation.navigate('OptionSelection', {catalogItemId: params.catalogItemId, optionSelections})}
          style={styles.actionButton}>
          {optionSummary}
        </Button>

        {isEditMode && editLine.instances?.length > 0 && (() => {
          const active = assignments.filter(a => a.status !== 'DELETED' && a.status !== 'COMPLETED');
          const completed = assignments.filter(a => a.status === 'COMPLETED');
          const hasAssignments = active.length > 0 || completed.length > 0;
          const allCompleted = active.length === 0 && completed.length > 0;

          let icon, iconColor, label, borderStyle, borderColor, leftAccent, bgColor;

          if (!hasAssignments) {
            icon = 'account-hard-hat';
            iconColor = colors.textSecondary;
            label = 'Assign Worker';
            borderStyle = 'dashed';
            borderColor = colors.textSecondary;
          } else if (allCompleted) {
            icon = 'check-circle';
            iconColor = colors.success;
            label = `All completed (${completed.length})`;
            borderColor = colors.success;
            bgColor = '#E8F5E9';
          } else if (active.length === 1) {
            const a = active[0];
            if (a.status === 'STARTED' || a.status === 'IN_PROGRESS') {
              icon = 'progress-wrench';
              iconColor = colors.primary;
              label = `${a.workerName} \u00B7 In Progress`;
              borderColor = colors.primary;
              leftAccent = true;
            } else if (a.status === 'COMPLETED') {
              icon = 'check-circle';
              iconColor = colors.success;
              label = `${a.workerName} \u00B7 Completed`;
              borderColor = colors.success;
            } else {
              icon = 'clock-outline';
              iconColor = colors.textSecondary;
              label = `${a.workerName} \u00B7 Pending`;
              borderColor = colors.textSecondary;
            }
          } else {
            // Multiple active
            icon = 'progress-wrench';
            iconColor = colors.primary;
            label = `${active.length} workers active`;
            borderColor = colors.primary;
          }

          return (
            <TouchableOpacity
              style={[
                styles.assignCard,
                {borderColor: borderColor || colors.border, borderStyle: borderStyle || 'solid'},
                leftAccent && styles.assignCardLeftAccent,
                bgColor && {backgroundColor: bgColor},
              ]}
              onPress={() => setAssignModalVisible(true)}
              activeOpacity={0.7}>
              <Icon name={icon} size={22} color={iconColor} style={{marginRight: spacing.sm}} />
              <Text style={[
                styles.assignCardText,
                !hasAssignments && {color: colors.primary},
                allCompleted && {color: colors.success},
              ]} numberOfLines={1}>
                {label}
              </Text>
              {completed.length > 0 && active.length > 0 && (
                <View style={styles.assignBadge}>
                  <Text style={styles.assignBadgeText}>+{completed.length} done</Text>
                </View>
              )}
              <Icon name="chevron-right" size={20} color={colors.textSecondary} />
            </TouchableOpacity>
          );
        })()}

        <Divider style={styles.divider} />

        {/* Special Instructions */}
        <Text variant="titleSmall" style={styles.sectionLabel}>Special Instructions</Text>
        <TextInput value={specialInstructions} onChangeText={setSpecialInstructions}
          mode="outlined" style={styles.input} multiline numberOfLines={3}
          outlineColor={colors.border} activeOutlineColor={colors.primary} />

        {/* Audio Instructions */}
        {isEditMode && audioInstructions.length > 0 && audioInstructions.map((audio, idx) => (
          <View key={idx} style={styles.audioFileRow}>
            <TouchableOpacity onPress={() => handlePlayAudio(audio.url, idx)}>
              <IconButton
                icon={playingIndex === idx ? 'stop-circle' : 'play-circle'}
                size={24}
                iconColor={playingIndex === idx ? colors.error : colors.primary}
                style={{margin: 0}}
              />
            </TouchableOpacity>
            <View style={styles.audioFileInfo}>
              <Text style={styles.audioFileName} numberOfLines={1}>
                {playingIndex === idx ? 'Playing...' : (audio.note || `Audio ${idx + 1}`)}
              </Text>
              {audio.note && playingIndex !== idx ? (
                <Text style={styles.audioFileLabel} numberOfLines={1}>Audio {idx + 1}</Text>
              ) : null}
            </View>
            <IconButton icon="pencil-outline" size={18} iconColor={colors.textSecondary} style={{margin: 0}}
              onPress={() => openNoteEditor(idx, audio.note)} />
            <IconButton icon="delete-outline" size={20} iconColor={colors.error} style={{margin: 0}}
              onPress={() => handleDeleteAudio(idx)} />
          </View>
        ))}
        {isEditMode && audioInstructions.length < 3 && (
          <TouchableOpacity style={styles.audioRow} onPress={openAudioModal}>
            <IconButton icon="microphone" size={24} iconColor={colors.primary} style={{margin: 0}} />
            <Text style={styles.audioText}>Record Instructions Audio</Text>
          </TouchableOpacity>
        )}
        {!isEditMode && (
          <View style={styles.audioHint}>
            <IconButton icon="microphone" size={20} iconColor={colors.textSecondary} style={{margin: 0}} />
            <Text style={styles.audioHintText}>Audio recording available after saving</Text>
          </View>
        )}

        {/* Video Link */}
        <Text variant="titleSmall" style={styles.sectionLabel}>Video Link</Text>
        <TextInput value={videoLink} onChangeText={setVideoLink}
          mode="outlined" style={styles.input} placeholder="https://..."
          outlineColor={colors.border} activeOutlineColor={colors.primary} />

        <Divider style={styles.divider} />

        {/* Pricing */}
        <View style={styles.blockCard}>
          <Text variant="titleSmall" style={styles.blockLabel}>PRICING</Text>

          {/* Header row */}
          {!isEditMode && (
            <View style={styles.pricingHeaderRow}>
              <Text style={styles.pricingHeaderQty}>Qty<Text style={styles.mandatory}> *</Text></Text>
              <Text style={styles.pricingHeaderCost}>Cost / Item<Text style={styles.mandatory}> *</Text></Text>
              <Text style={styles.pricingHeaderTotal}>Total</Text>
            </View>
          )}

          {/* Stitching row */}
          <View style={styles.pricingRow}>
            {!isEditMode ? (
              <View style={styles.pricingQtyCol}>
                <IconButton icon="minus" size={16} onPress={() => setQuantity(Math.max(1, quantity - 1))}
                  style={styles.pricingQtyBtn} iconColor={colors.primary} />
                <TextInput
                  value={String(quantity)}
                  onChangeText={v => {
                    const num = parseInt(v, 10);
                    setQuantity(num > 0 ? num : 1);
                  }}
                  mode="outlined" dense keyboardType="numeric"
                  style={styles.pricingQtyInput} outlineColor={colors.border} activeOutlineColor={colors.primary}
                />
                <IconButton icon="plus" size={16} onPress={() => setQuantity(quantity + 1)}
                  style={styles.pricingQtyBtn} iconColor={colors.primary} />
              </View>
            ) : (
              <Text variant="bodyMedium" style={styles.editPriceLabel}>Price for item<Text style={styles.mandatory}> *</Text></Text>
            )}
            <View style={isEditMode ? styles.editPriceInput : styles.pricingCostCol}>
              <TextInput value={unitPrice} onChangeText={setUnitPrice}
                mode="outlined" dense keyboardType="numeric" placeholder="0"
                style={styles.pricingInput} outlineColor={colors.border} activeOutlineColor={colors.primary} />
            </View>
            {!isEditMode && (
              <Text style={styles.pricingTotalText}>{'\u20B9'}{stitchingTotal.toLocaleString()}</Text>
            )}
          </View>

          {/* Add-ons row */}
          <TouchableOpacity style={styles.pricingAddOnTouchable} onPress={() =>
            navigation.navigate('AddOnSelector', {selectedAddOns, returnTo: 'OrderLineDetail'})}>
            {selectedAddOns.length > 0 ? (
              <View style={styles.pricingRow}>
                <View style={isEditMode ? {width: '75%'} : styles.pricingQtyCol}>
                  <Text style={styles.pricingAddOnNames} numberOfLines={2}>
                    {selectedAddOns.length} add-on{selectedAddOns.length > 1 ? 's' : ''}: {selectedAddOns.map(a => a.name).join(', ')}
                  </Text>
                </View>
                {!isEditMode ? (
                  <>
                    <Text style={styles.pricingAddOnUnit}>{'\u20B9'}{addOnsTotal.toLocaleString()}</Text>
                    <Text style={styles.pricingTotalText}>{'\u20B9'}{(addOnsTotal * quantity).toLocaleString()}</Text>
                  </>
                ) : (
                  <Text style={[styles.pricingAddOnUnit, {width: '25%', textAlign: 'right'}]}>{'\u20B9'}{addOnsTotal.toLocaleString()}</Text>
                )}
              </View>
            ) : (
              <Text style={styles.addOnLabel}>+ Add Ons</Text>
            )}
          </TouchableOpacity>

          {/* Grand total */}
          {!isEditMode ? (
            <>
              <Divider style={{marginVertical: spacing.xs}} />
              <View style={styles.pricingGrandTotalRow}>
                <Text style={styles.pricingGrandTotalLabel}>Total</Text>
                <Text style={styles.pricingGrandTotalValue}>
                  {'\u20B9'}{((stitchingTotal + addOnsTotal * quantity)).toLocaleString()}
                </Text>
              </View>
            </>
          ) : selectedAddOns.length > 0 && (
            <>
              <Divider style={{marginVertical: spacing.xs}} />
              <View style={styles.pricingGrandTotalRow}>
                <Text style={styles.pricingGrandTotalLabel}>Total</Text>
                <Text style={styles.pricingGrandTotalValue}>
                  {'\u20B9'}{((parseFloat(unitPrice) || 0) + addOnsTotal).toLocaleString()}
                </Text>
              </View>
            </>
          )}
        </View>

        {/* Dates — two-column layout */}
        <View style={styles.blockCard}>
          <Text variant="titleSmall" style={styles.blockLabel}>DATES</Text>

          {trialDates.map((td, idx) => (
            <View key={idx} style={styles.dateRow}>
              <Text style={styles.dateLabel}>Trial {trialDates.length > 1 ? idx + 1 : ''}</Text>
              <View style={styles.dateValueWithAction}>
                <View style={{flex: 1}}>
                  <DatePickerInput
                    label={`Trial Date ${idx + 1}`}
                    value={td}
                    onChangeText={v => updateTrialDate(idx, v)}
                    minDate={params.orderDate}
                    maxDate={deliveryDate || undefined}
                  />
                </View>
                <IconButton icon="close-circle" size={18} onPress={() => removeTrialDate(idx)} iconColor={colors.error} style={{margin: 0}} />
              </View>
            </View>
          ))}
          <View style={styles.dateRow}>
            <Text style={styles.dateLabel}>{trialDates.length === 0 ? 'Trial' : ''}</Text>
            <View style={styles.dateValue}>
              <Button mode="text" icon="plus" onPress={addTrialDate} compact textColor={colors.primary} style={{alignSelf: 'flex-start'}}>
                Add Trial Date
              </Button>
            </View>
          </View>

          <View style={styles.dateRow}>
            <Text style={styles.dateLabel}>Delivery<Text style={styles.mandatory}> *</Text></Text>
            <View style={styles.dateValue}>
              <DatePickerInput
                label="Delivery Date *"
                value={deliveryDate}
                onChangeText={setDeliveryDate}
                minDate={params.orderDate}
              />
            </View>
          </View>

          <View style={styles.dateRow}>
            <Text style={styles.dateLabel}>Function</Text>
            <View style={styles.dateValue}>
              <DatePickerInput
                label="Function Date"
                value={functionDate}
                onChangeText={setFunctionDate}
                minDate={params.orderDate}
              />
            </View>
          </View>
        </View>

        {/* Urgent */}
        <View style={styles.switchRow}>
          <Text variant="bodyMedium">Mark as Urgent</Text>
          <Switch value={urgentLine} onValueChange={setUrgentLine} color={colors.error} />
        </View>

        {/* Measurement Dress Given */}
        <View style={styles.switchRow}>
          <Text variant="bodyMedium">Measurement dress given?</Text>
          <Switch value={measurementDressGiven} onValueChange={setMeasurementDressGiven} color={colors.primary} />
        </View>

        {/* Sales Staff */}
        <View style={styles.salesStaffRow}>
          <Text variant="bodyMedium" style={styles.salesStaffLabel}>Sales Staff</Text>
          <TouchableOpacity style={styles.salesStaffAction} onPress={() =>
            navigation.navigate('SalesStaffSelector', {returnTo: 'OrderLineDetail'})}>
            {salesStaffName ? (
              <View style={styles.salesStaffSelected}>
                <Text variant="bodyMedium" style={styles.salesStaffName}>{salesStaffName}</Text>
                <IconButton icon="pencil" size={16} iconColor={colors.primary} style={{margin: 0}} />
              </View>
            ) : (
              <Text style={styles.salesStaffChoose}>Choose</Text>
            )}
          </TouchableOpacity>
        </View>

        <Divider style={styles.divider} />

        {/* Cloth Images */}
        <Text variant="titleSmall" style={styles.sectionLabel}>Cloth Images</Text>
        <View style={styles.imageRow}>
          {clothImages.map((img, idx) => (
            <View key={idx} style={styles.imageContainer}>
              <Image source={{uri: img.uri}} style={styles.imagePreview} />
              <TouchableOpacity style={styles.imageDeleteBtn}
                onPress={() => setClothImages(prev => prev.filter((_, i) => i !== idx))}>
                <IconButton icon="close-circle" size={18} iconColor={colors.error} style={{margin: 0}} />
              </TouchableOpacity>
            </View>
          ))}
          {clothImages.length < 2 && (
            <TouchableOpacity style={styles.imagePlaceholder} onPress={() => pickImage(setClothImages)}>
              <Text style={styles.imagePlaceholderText}>+ Add Photo</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Pattern Images */}
        <Text variant="titleSmall" style={styles.sectionLabel}>Pattern Images</Text>
        <View style={styles.imageRow}>
          {patternImages.map((img, idx) => (
            <View key={idx} style={styles.imageContainer}>
              <Image source={{uri: img.uri}} style={styles.imagePreview} />
              <TouchableOpacity style={styles.imageDeleteBtn}
                onPress={() => setPatternImages(prev => prev.filter((_, i) => i !== idx))}>
                <IconButton icon="close-circle" size={18} iconColor={colors.error} style={{margin: 0}} />
              </TouchableOpacity>
            </View>
          ))}
          {patternImages.length < 2 && (
            <TouchableOpacity style={styles.imagePlaceholder} onPress={() => pickImage(setPatternImages)}>
              <Text style={styles.imagePlaceholderText}>+ Add Photo</Text>
            </TouchableOpacity>
          )}
        </View>

        <View style={{height: spacing.xxl}} />
      </ScrollView>

      <View style={styles.bottomBar}>
        <Button mode="contained" onPress={handleSave} loading={saving} disabled={saving}
          style={styles.bottomButton} buttonColor={colors.primary}>
          {isEditMode ? 'Update Item' : 'Save Item'}
        </Button>
      </View>

      {isEditMode && editLine.instances?.length > 0 && (
        <WorkAssignmentModal
          visible={assignModalVisible}
          onDismiss={() => {
            setAssignModalVisible(false);
            fetchAssignments();
          }}
          orderLineInstanceId={editLine.instances[0].id}
          catalogItemId={params.catalogItemId}
          serviceCatalogItemId={serviceCatalogItemId}
        />
      )}

      {/* Audio Recording Modal */}
      <Portal>
        <Dialog visible={audioModalVisible} onDismiss={cancelRecording} style={styles.audioDialog}>
          <Dialog.Title>Record Audio</Dialog.Title>
          <Dialog.Content style={styles.audioDialogContent}>
            {audioUploading ? (
              <View style={styles.audioCenter}>
                <ActivityIndicator size="large" color={colors.primary} />
                <Text style={styles.audioStatusText}>Uploading audio...</Text>
              </View>
            ) : isRecording ? (
              <View style={styles.audioCenter}>
                <View style={styles.recordingIndicator}>
                  <View style={styles.recordingDot} />
                  <Text style={styles.recordingLabel}>Recording</Text>
                </View>
                <Text style={styles.audioDuration}>{recordingDuration}</Text>
              </View>
            ) : (
              <View style={styles.audioCenter}>
                <IconButton icon="microphone" size={48} iconColor={colors.primary} />
                <Text style={styles.audioStatusText}>Tap Start to begin recording</Text>
              </View>
            )}
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={cancelRecording} textColor={colors.textSecondary} disabled={audioUploading}>Cancel</Button>
            {!isRecording && !audioUploading && (
              <Button mode="contained" onPress={startRecording} style={styles.audioActionBtn}
                buttonColor={colors.primary}>Start</Button>
            )}
            {isRecording && (
              <Button mode="contained" onPress={stopAndUpload} style={styles.audioActionBtn}
                buttonColor={colors.success}>Save</Button>
            )}
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {/* Audio Note Dialog */}
      <Portal>
        <Dialog visible={noteDialogVisible} onDismiss={() => setNoteDialogVisible(false)} style={styles.audioDialog}>
          <Dialog.Title>Audio Note</Dialog.Title>
          <Dialog.Content>
            <TextInput
              mode="outlined"
              placeholder="E.g. Collar style instructions"
              value={noteText}
              onChangeText={setNoteText}
              style={{backgroundColor: colors.surface}}
              outlineColor={colors.border}
              activeOutlineColor={colors.primary}
              autoFocus
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setNoteDialogVisible(false)} textColor={colors.textSecondary}>Skip</Button>
            <Button mode="contained" onPress={handleSaveNote} buttonColor={colors.primary}
              style={styles.audioActionBtn}>Save</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  content: {flex: 1, padding: spacing.md},
  sectionLabel: {fontWeight: '600', color: colors.text, marginBottom: spacing.xs, marginTop: spacing.sm},
  mandatory: {color: colors.error, fontWeight: '700'},
  blockCard: {
    backgroundColor: colors.surface, borderRadius: 10, padding: spacing.md,
    marginVertical: spacing.sm, elevation: 2,
    shadowColor: '#000', shadowOffset: {width: 0, height: 1}, shadowOpacity: 0.1, shadowRadius: 3,
    borderWidth: 1, borderColor: colors.border,
  },
  blockLabel: {fontWeight: '700', color: colors.textSecondary, marginBottom: spacing.sm, fontSize: 12, letterSpacing: 1},
  input: {marginBottom: spacing.sm, backgroundColor: colors.surface},
  serviceTypeRow: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.sm},
  serviceTypeOption: {
    flexDirection: 'row', alignItems: 'center',
    borderWidth: 1, borderColor: colors.border, borderRadius: 8,
    paddingRight: spacing.md, backgroundColor: colors.surface,
  },
  serviceTypeOptionSelected: {borderColor: colors.primary, backgroundColor: '#E8F5E9'},
  serviceTypeIcon: {margin: 0},
  serviceTypeLabel: {fontSize: 14, color: colors.textSecondary},
  serviceTypeLabelSelected: {color: colors.primary, fontWeight: '600'},
  divider: {marginVertical: spacing.md},
  salesStaffRow: {flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: spacing.md, marginBottom: spacing.sm},
  salesStaffLabel: {fontWeight: '500', color: colors.text},
  salesStaffAction: {alignItems: 'flex-end'},
  salesStaffSelected: {flexDirection: 'row', alignItems: 'center'},
  salesStaffName: {color: colors.primary, fontWeight: '500'},
  salesStaffChoose: {color: colors.primary, fontWeight: '600', fontSize: 14},
  actionButton: {marginBottom: spacing.sm, borderColor: colors.primary},
  assignCard: {
    flexDirection: 'row', alignItems: 'center',
    borderWidth: 1, borderRadius: 8,
    paddingVertical: spacing.sm, paddingHorizontal: spacing.md,
    marginBottom: spacing.sm, backgroundColor: colors.surface,
  },
  assignCardLeftAccent: {borderLeftWidth: 3, borderLeftColor: colors.primary},
  assignCardText: {flex: 1, fontSize: 14, fontWeight: '500', color: colors.text},
  assignBadge: {
    backgroundColor: '#E8F5E9', borderRadius: 12,
    paddingHorizontal: spacing.sm, paddingVertical: 2, marginRight: spacing.xs,
  },
  assignBadgeText: {fontSize: 11, fontWeight: '600', color: colors.success},
  audioRow: {flexDirection: 'row', alignItems: 'center', marginVertical: spacing.xs},
  audioText: {color: colors.primary, fontWeight: '500', marginLeft: spacing.xs},
  audioFileRow: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: colors.surface, borderRadius: 8, borderWidth: 1, borderColor: colors.border,
    paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, marginBottom: spacing.xs,
  },
  audioFileInfo: {flex: 1, marginLeft: spacing.xs},
  audioFileName: {fontSize: 13, color: colors.text},
  audioFileLabel: {fontSize: 11, color: colors.textSecondary, marginTop: 1},
  audioHint: {flexDirection: 'row', alignItems: 'center', marginVertical: spacing.xs},
  audioHintText: {color: colors.textSecondary, fontSize: 13, marginLeft: spacing.xs},
  audioDialog: {borderRadius: 15},
  audioDialogContent: {minHeight: 120, justifyContent: 'center'},
  audioCenter: {alignItems: 'center', justifyContent: 'center'},
  audioStatusText: {color: colors.textSecondary, marginTop: spacing.sm, fontSize: 14},
  recordingIndicator: {flexDirection: 'row', alignItems: 'center'},
  recordingDot: {width: 12, height: 12, borderRadius: 6, backgroundColor: colors.error, marginRight: spacing.xs},
  recordingLabel: {color: colors.error, fontWeight: '600', fontSize: 16},
  audioDuration: {fontSize: 32, fontWeight: '700', color: colors.text, marginTop: spacing.sm},
  audioActionBtn: {borderRadius: 8, minWidth: 80},
  switchRow: {flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginVertical: spacing.sm},
  pricingHeaderRow: {flexDirection: 'row', alignItems: 'center', marginBottom: spacing.xs},
  pricingHeaderQty: {width: '45%', fontSize: 12, fontWeight: '600', color: colors.textSecondary, textTransform: 'uppercase'},
  pricingHeaderCost: {width: '30%', fontSize: 12, fontWeight: '600', color: colors.textSecondary, textTransform: 'uppercase'},
  pricingHeaderTotal: {width: '25%', fontSize: 12, fontWeight: '600', color: colors.textSecondary, textTransform: 'uppercase', textAlign: 'right'},
  pricingRow: {flexDirection: 'row', alignItems: 'center', marginBottom: spacing.xs},
  pricingQtyCol: {width: '45%', flexDirection: 'row', alignItems: 'center'},
  pricingQtyBtn: {borderWidth: 1, borderColor: colors.border, borderRadius: 4, margin: 0, width: 28, height: 28},
  pricingQtyInput: {width: 48, textAlign: 'center', backgroundColor: colors.surface, marginHorizontal: 4},
  editPriceLabel: {flex: 1, fontWeight: '500', color: colors.text},
  editPriceInput: {width: '40%'},
  pricingCostCol: {width: '30%'},
  pricingInput: {backgroundColor: colors.surface},
  pricingTotalText: {width: '25%', textAlign: 'right', fontWeight: '600', fontSize: 15, color: colors.text},
  pricingAddOnTouchable: {paddingVertical: spacing.xs},
  pricingAddOnNames: {fontSize: 13, color: colors.primary, fontWeight: '500', paddingRight: 15},
  pricingAddOnUnit: {width: '30%', textAlign: 'left', fontSize: 13, color: colors.textSecondary},
  addOnLabel: {color: colors.primary, fontWeight: '600'},
  pricingGrandTotalRow: {flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: spacing.xs},
  pricingGrandTotalLabel: {fontSize: 15, fontWeight: '700', color: colors.text},
  pricingGrandTotalValue: {fontSize: 16, fontWeight: '700', color: colors.primary},
  dateRow: {flexDirection: 'row', alignItems: 'center', marginBottom: spacing.xs},
  dateLabel: {width: '40%', fontSize: 14, fontWeight: '500', color: colors.textSecondary},
  dateValue: {width: '60%'},
  dateValueWithAction: {width: '60%', flexDirection: 'row', alignItems: 'center'},
  imageRow: {flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.sm},
  imageContainer: {position: 'relative'},
  imagePreview: {width: 80, height: 80, borderRadius: 8, backgroundColor: colors.border},
  imageDeleteBtn: {position: 'absolute', top: -8, right: -8},
  imagePlaceholder: {
    width: 80, height: 80, borderRadius: 8, borderWidth: 1, borderColor: colors.border,
    borderStyle: 'dashed', justifyContent: 'center', alignItems: 'center', backgroundColor: colors.surface,
  },
  imagePlaceholderText: {color: colors.textSecondary, fontSize: 11, textAlign: 'center'},
  bottomBar: {
    flexDirection: 'row', padding: spacing.md, backgroundColor: colors.surface,
    gap: spacing.sm, elevation: 4,
  },
  bottomButton: {flex: 1, borderRadius: 8},
});

export default OrderLineDetailScreen;
